/**
 * Centralized tracking utility for sending data to both Facebook Pixel and Statsig
 */

let statsigClient = null;


// Enhanced function to get Facebook cookies and URL parameters for better match quality
function getFacebookTrackingData() {
  // First try to get from URL parameters (passed from Typeform redirect)
  const urlParams = new URLSearchParams(window.location.search);
  let fbp = urlParams.get('fbp');
  let fbc = urlParams.get('fbc');
  let fbclid = urlParams.get('fbclid');
  
  // If not in URL parameters, try to get from cookies
  if (!fbp) {
    fbp = document.cookie.match(/_fbp=([^;]+)/)?.[1] || null;
  }
  if (!fbc) {
    fbc = document.cookie.match(/_fbc=([^;]+)/)?.[1] || null;
  }
  
  // If we have fbclid from URL but no fbc, build fbc
  if (fbclid && !fbc) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
  }
  
  // Set/update cookies if we got data from URL parameters (reconstruct tracking)
  if (fbp && !document.cookie.includes('_fbp=')) {
    document.cookie = `_fbp=${fbp}; path=/; domain=${window.location.hostname}; max-age=7776000`; // 90 days
  }
  if (fbc && !document.cookie.includes('_fbc=')) {
    document.cookie = `_fbc=${fbc}; path=/; domain=${window.location.hostname}; max-age=7776000`; // 90 days
  }
  
  return { fbp, fbc };
}

// Function to get user agent and other browser data
function getBrowserData() {
  return {
    user_agent: navigator.userAgent,
    url: window.location.href,
    referrer: document.referrer || undefined
  };
}

/**
 * Extract tracking parameters from URL
 * Captures Facebook, session, and UTM parameters for enhanced attribution
 */
export function getTrackingParamsFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const trackingParams = {};
  
  // Facebook tracking parameters
  const { fbp, fbc } = getFacebookTrackingData();
  const { user_agent, url, referrer } = getBrowserData();
  const fbclid = urlParams.get('fbclid');
  
  // Session tracking
  const sid = urlParams.get('sid');
  
  // UTM parameters
  const utm_source = urlParams.get('utm_source');
  const utm_campaign = urlParams.get('utm_campaign');
  const utm_medium = urlParams.get('utm_medium');
  const utm_term = urlParams.get('utm_term');
  const utm_content = urlParams.get('utm_content');
  
  // Only include parameters that are present
  if (user_agent) trackingParams.user_agent = user_agent;
  if (url) trackingParams.url = url;
  if (referrer) trackingParams.referrer = referrer;
  if (fbp) trackingParams.fbp = fbp;
  if (fbc) trackingParams.fbc = fbc;
  if (fbclid) trackingParams.fbclid = fbclid;
  if (sid) trackingParams.sid = sid;
  if (utm_source) trackingParams.utm_source = utm_source;
  if (utm_campaign) trackingParams.utm_campaign = utm_campaign;
  if (utm_medium) trackingParams.utm_medium = utm_medium;
  if (utm_term) trackingParams.utm_term = utm_term;
  if (utm_content) trackingParams.utm_content = utm_content;
  
  return trackingParams;
}

/**
 * Tracking action types enum
 * Prevents typos and ensures only valid tracking actions are used
 */
export const TRACKING_ACTIONS = {
  // Quiz and Assessment Actions
  QUIZ_START: 'QUIZ_START',
  QUIZ_RESULTS_VIEWED: 'QUIZ_RESULTS_VIEWED',
  
  // Contact Actions (High Intent)
  WHATSAPP_BUTTON: 'WHATSAPP_BUTTON',
  CUSTOM_QUESTION: 'CUSTOM_QUESTION',
  PHONE_CALL: 'PHONE_CALL',
  WHATSAPP_MENU: 'WHATSAPP_MENU',
  
  // Footer Contact Actions
  FOOTER_PHONE: 'FOOTER_PHONE',
  FOOTER_EMAIL: 'FOOTER_EMAIL',
  FOOTER_LOCATION: 'FOOTER_LOCATION',
  
  // Service Interest Actions
  SLEEP_STUDY_PAGE: 'SLEEP_STUDY_PAGE',
  
  // Information Seeking Actions
  LOCATION_VISIT: 'LOCATION_VISIT',
  WEBSITE_VISIT: 'WEBSITE_VISIT',
  
  // Social Media Engagement
  SOCIAL_ENGAGEMENT: 'SOCIAL_ENGAGEMENT',
};

/**
 * Centralized tracking values configuration
 * All monetary values are in MXN (Mexican Pesos)
 */
export const TRACKING_VALUES = {
  [TRACKING_ACTIONS.QUIZ_START]: 5,
  [TRACKING_ACTIONS.SLEEP_STUDY_PAGE]: 20,
  [TRACKING_ACTIONS.WHATSAPP_MENU]: 50,
  [TRACKING_ACTIONS.LOCATION_VISIT]: 10,
  [TRACKING_ACTIONS.WEBSITE_VISIT]: 5,
  [TRACKING_ACTIONS.SOCIAL_ENGAGEMENT]: 2,

  [TRACKING_ACTIONS.QUIZ_RESULTS_VIEWED]: 25,
  [TRACKING_ACTIONS.WHATSAPP_BUTTON]: 100,
  [TRACKING_ACTIONS.CUSTOM_QUESTION]: 100,
  [TRACKING_ACTIONS.PHONE_CALL]: 150,
  
  // Footer Contact Actions (High Value - Direct Contact from Results Page)
  [TRACKING_ACTIONS.FOOTER_PHONE]: 120,
  [TRACKING_ACTIONS.FOOTER_EMAIL]: 110,
  [TRACKING_ACTIONS.FOOTER_LOCATION]: 15,
};

// Initialize Statsig client reference
export function initializeTracking(client) {
  statsigClient = client;
}

/**
 * Universal tracking function that sends data to both Facebook Pixel and Statsig
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventData - The data associated with the event
 * @param {Object} options - Additional options (eventID, value, currency, etc.)
 * @param {Object} client - Optional Statsig client to use (overrides global)
 */
export function trackEvent(eventName, eventData = {}, options = {}, client = null) {
  try {
    // Get URL tracking parameters
    const urlTrackingParams = getTrackingParamsFromURL();
    
    // Merge event data with URL tracking parameters
    const enrichedEventData = {
      ...eventData,
      ...urlTrackingParams,
      timestamp: new Date().toISOString()
    };

    // Track with Statsig (use passed client or global)
    const clientToUse = client || statsigClient;
    if (clientToUse) {
      clientToUse.logEvent(eventName, eventName, enrichedEventData);
    } else {
      console.warn('Statsig client not initialized for tracking');
    }

    // Track with Facebook Pixel
    if (typeof window.fbq === 'function') {
      const fbqOptions = {};
      
      // Add eventID if provided to prevent duplicate tracking
      if (options.eventID) {
        fbqOptions.eventID = options.eventID;
      }

      // If value and currency are provided, include them in the event data
      const fbqEventData = { ...enrichedEventData };
      if (options.value !== undefined) {
        fbqEventData.value = options.value;
      }
      if (options.currency) {
        fbqEventData.currency = options.currency;
      }

      if (eventName === 'Lead') {
        window.fbq('track', 'Lead', fbqEventData, fbqOptions);
      } else {
        window.fbq('trackCustom', eventName, fbqEventData, fbqOptions);
      }
    } else {
      console.warn('Facebook Pixel not available for tracking');
    }

    // Track with Google
    // if (typeof window.gtag === 'function') {
    // console.log('About to track with Google');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
    // console.log('Tracked with Google');
    // } else {
    //   console.warn('Google Tag Manager not available for tracking');
    // }
  } catch (error) {
    console.error(`Tracking error for event "${eventName}":`, error);
  }
}

/**
 * Specialized tracking function for menu actions
 * @param {string} step - The step/action taken
 * @param {string} actionType - Value from TRACKING_ACTIONS enum
 * @param {Object} client - Optional Statsig client to use
 */
export function trackLead(step, actionType, client = null) {
  return;
  // const value = TRACKING_VALUES[actionType] || 0;
  // trackEvent('Lead', {
  //   step: step,
  //   value: value,
  //   currency: 'MXN',
  //   variant: 'vo_test'
  // }, {
  //   eventID: 'menu.' + Date.now(),
  //   value: value,
  //   currency: 'MXN'
  // }, client);
}

/**
 * Specialized tracking function for contact actions
 * @param {string} step - The step/action taken
 * @param {string} actionType - Value from TRACKING_ACTIONS enum
 * @param {Object} additionalData - Any additional data to track
 * @param {Object} client - Optional Statsig client to use
 */
export function trackContactAction(step, actionType, additionalData = {}, client = null) {
  // Track contact action
  trackEvent('ContactAction', {
    step: step,
    ...additionalData
  }, {
    eventID: 'contact.' + Date.now(),
  }, client);
  trackLead(step, actionType, client);
}

/**
 * Specialized tracking function for quiz results
 * @param {Object} quizData - Quiz completion data
 * @param {string} eventID - Optional event ID for deduplication
 * @param {Object} client - Optional Statsig client to use
 */
export function trackQuizResults(quizData, eventID = null, client = null) {
  const additionalData = {
    quiz_type: 'apnea_quiz',
    content_category: 'Quiz',
    content_name: 'Apnea Risk Assessment',
    quiz_completed: true,
    step: 'page_view',
    ...quizData
  };
  
  trackEvent('ApneaQuizResultsViewed', additionalData, {
    eventID: eventID || 'quiz_results.' + Date.now(),
  }, client);

  trackLead('quiz_results', TRACKING_ACTIONS.QUIZ_RESULTS_VIEWED, client);
} 